import moment from 'moment'
import chartDataService from '../services/chart_data_service'

const STORAGE_KEY = 'district_dashboard_chart_filters'
const FILTER_DEBOUNCE_MS = 1000
const VISIBILITY_ROOT_MARGIN = '0px'

function chartDataUrlFromContainer() {
  const container = typeof document !== 'undefined' ? document.getElementById('vue-container') : null
  return container ? container.dataset.chartDataUrl : ''
}

function readAllSavedFilters() {
  try {
    return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY)) || {}
  } catch (error) {
    return {}
  }
}

function emptyTemplates() {
  return { sessionTemplateIds: [] }
}

function hasTemplateSelection(templates) {
  return templates.sessionTemplateIds.length > 0
}

function savedFiltersKey(chartDataUrl, yearInfo, section) {
  const year = yearInfo ? yearInfo.current_schools_year : ''
  return `${chartDataUrl}::${year}::${section}`
}

function selectedOptionsForType(templates, type, selectedIds) {
  return templates.filter((option) => (
    option.type === type && option.ids.length > 0 && option.ids.every((id) => selectedIds.includes(id))
  ))
}

function idsFromOptions(options) {
  return options.reduce((ids, option) => ids.concat(option.ids), [])
}

function validTemplates(templates, savedTemplates) {
  const saved = { ...emptyTemplates(), ...(savedTemplates || {}) }
  return {
    sessionTemplateIds: idsFromOptions(selectedOptionsForType(templates, 'session', saved.sessionTemplateIds))
  }
}

function formatIsoDate(value) {
  return moment(value).format('MM/DD/YYYY')
}

function writeSavedFilters(key, filters) {
  const savedByKey = readAllSavedFilters()
  const active = {}
  if (filters.dateRange) active.dateRange = filters.dateRange
  if (hasTemplateSelection(filters.templates)) active.templates = filters.templates
  if (filters.roles.length) active.roles = filters.roles

  if (Object.keys(active).length) {
    savedByKey[key] = active
  } else {
    delete savedByKey[key]
  }
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(savedByKey))
  } catch (error) {
    return
  }
}

export default {
  data() {
    const chartDataUrl = chartDataUrlFromContainer()
    const storageKey = savedFiltersKey(chartDataUrl, this.year_info, this.section)
    const saved = readAllSavedFilters()[storageKey] || {}
    return {
      chartDataUrl,
      storageKey,
      chartActivated: false,
      isLoading: true,
      initialLoadComplete: false,
      error: false,
      reqSeq: 0,
      fetchTimer: null,
      filters: {
        dateRange: saved.dateRange || null,
        templates: validTemplates(this.templates || [], saved.templates),
        roles: saved.roles || []
      }
    }
  },
  computed: {
    presetsDisabled() {
      return !!this.filters.dateRange
    },
    activeFilterCount() {
      return [
        !!this.filters.dateRange,
        hasTemplateSelection(this.filters.templates),
        this.filters.roles.length > 0
      ].filter(Boolean).length
    },
    anyFilterActive() {
      return this.activeFilterCount > 0
    },
    presetsDisabledTitle() {
      return this.presetsDisabled ? 'A date range filter is active. Clear it to use these ranges.' : null
    },
    selectedTemplateOptions() {
      return selectedOptionsForType(this.templates || [], 'session', this.filters.templates.sessionTemplateIds)
    },
    appliedFilters() {
      const applied = []
      const range = this.filters.dateRange
      if (range) {
        applied.push({
          key: 'dateRange',
          type: 'dateRange',
          label: `${formatIsoDate(range.startDate)} - ${formatIsoDate(range.endDate)}`
        })
      }
      this.selectedTemplateOptions.forEach((option) => {
        applied.push({ key: `template:${option.ids.join(',')}`, type: 'template', label: option.label, ids: option.ids })
      })
      this.filters.roles.forEach((role) => {
        applied.push({ key: `role:${role}`, type: 'role', label: this.roleLabel(role), value: role })
      })
      return applied
    },
    appliedFiltersStyle() {
      return this.chartWidth ? { minWidth: `min(${this.chartWidth}px, 100%)` } : null
    },
    chartErrorMessage() {
      return "Couldn't update this chart. Showing the previous data."
    },
    isCurrentYear() {
      return this.year_info.is_current_school_year
    },
    chartReady() {
      return this.chartActivated && this.initialLoadComplete
    }
  },
  mounted() {
    writeSavedFilters(this.storageKey, this.filters)

    this.fetchWhenVisible()

    this.teardownOnLeave = () => {
      this.hasLeftPage = true
      clearTimeout(this.fetchTimer)
      this.visibilityObserver?.disconnect()
      document.removeEventListener('turbolinks:visit', this.teardownOnLeave)
    }
    document.addEventListener('turbolinks:visit', this.teardownOnLeave)
  },
  beforeDestroy() {
    this.teardownOnLeave?.()
  },
  methods: {
    fetchWhenVisible() {
      if (!('IntersectionObserver' in window)) {
        this.chartActivated = true
        this.fetchFirstChartData()
        return
      }

      this.visibilityObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return

        this.visibilityObserver.disconnect()
        this.chartActivated = true
        this.fetchFirstChartData()
      }, { rootMargin: VISIBILITY_ROOT_MARGIN })
      this.visibilityObserver.observe(this.$el)
    },
    invalidateInFlight() {
      this.reqSeq += 1
      this.isLoading = false
      this.error = false
    },
    onDateRangeChange(range) {
      if (!range && !this.filters.dateRange) return
      this.applyDateRangeFilter(range)
      this.persistAndFetch()
    },
    applyDateRangeFilter(range) {
      this.filters.dateRange = range
      if (!range) this.reapplyCurrentPreset()
    },
    roleLabel(value) {
      const option = (this.roles || []).find((role) => role.value === value)
      return option ? option.label : value
    },
    removeFilter(filter) {
      if (filter.type === 'dateRange') {
        this.applyDateRangeFilter(null)
      } else if (filter.type === 'template') {
        this.filters.templates = {
          sessionTemplateIds: this.filters.templates.sessionTemplateIds.filter((id) => !filter.ids.includes(id))
        }
      } else {
        this.filters.roles = this.filters.roles.filter((role) => role !== filter.value)
      }
      this.persistAndFetch({ immediate: true })
    },
    onTemplatesChange(templates) {
      this.filters.templates = templates
      this.persistAndFetch()
    },
    onRolesChange(roles) {
      this.filters.roles = roles
      this.persistAndFetch()
    },
    persistAndFetch(options = {}) {
      writeSavedFilters(this.storageKey, this.filters)
      clearTimeout(this.fetchTimer)
      this.invalidateInFlight()
      if (!this.anyFilterActive) {
        this.reapplyCurrentPreset()
        this.fetchChartData()
        return
      }
      if (options.immediate) {
        this.fetchChartData()
        return
      }
      this.isLoading = true
      this.fetchTimer = setTimeout(() => this.fetchChartData(), FILTER_DEBOUNCE_MS)
    },
    clearAllFilters() {
      clearTimeout(this.fetchTimer)
      this.invalidateInFlight()
      this.filters.dateRange = null
      this.filters.templates = emptyTemplates()
      this.filters.roles = []
      writeSavedFilters(this.storageKey, this.filters)
      this.$refs.dateRangeFilter?.clear()
      this.reapplyCurrentPreset()
      this.fetchChartData()
    },
    applyDateRangeBounds() {
      if (!this.filters.dateRange) return
      this.startDate = moment(this.filters.dateRange.startDate).startOf('day')
      this.endDate = moment(this.filters.dateRange.endDate).endOf('day')
    },
    reapplyCurrentPreset() {
      if (this.currentFilterType === '7_days') {
        this.sevenDayRangeClicked?.()
      } else if (this.currentFilterType === '30_days') {
        this.thirtyDayRangeClicked?.()
      } else {
        this.wholeYearRangeClicked?.()
      }
    },
    requestParams() {
      const params = {
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }
      if (this.filters.dateRange) {
        params.start_date = this.filters.dateRange.startDate
        params.end_date = this.filters.dateRange.endDate
      }
      if (this.filters.templates.sessionTemplateIds.length) {
        params.session_template_ids = this.filters.templates.sessionTemplateIds.join(',')
      }
      if (this.filters.roles.length) {
        params.roles = this.filters.roles.join(",")
      }
      return params
    },
    fetchFirstChartData() {
      if (this.hasLeftPage) return

      const request = this.anyFilterActive ? chartDataService.requestChartData : chartDataService.requestGroupChartData
      this.receiveChartData(request(this.section, this.requestParams()))
    },
    fetchChartData() {
      if (this.hasLeftPage) return

      this.receiveChartData(chartDataService.requestChartData(this.section, this.requestParams()))
    },
    receiveChartData(request) {
      this.invalidateInFlight()
      this.isLoading = true
      const mySeq = this.reqSeq

      request
        .then((sectionData) => {
          if (mySeq !== this.reqSeq) return
          this.applyData(sectionData)
          this.isLoading = false
          this.initialLoadComplete = true
        })
        .catch(() => {
          if (mySeq !== this.reqSeq) return
          this.error = true
          this.isLoading = false
          this.initialLoadComplete = true
        })
    }
  }
}
